import {
  RetailerNetworkTobaccoProgramPerformanceFragment,
  RetailerNetworkTobaccoProgramMetricType,
} from '@occc/graphql';
import { MetricCardProps, MetricCardValueType } from '@occc/ui-design-system';

type CatalogEntry = {
  type: RetailerNetworkTobaccoProgramMetricType;
  label: string;
  /**
   * Optional full label for use in detailed views. If not provided, `label` will be used.
   */
  fullLabel?: string;
  valueType?: MetricCardValueType;
  valueSuffix?: string;
  valuePrefix?: string;
  getValue: (performance: RetailerNetworkTobaccoProgramPerformanceFragment) => number;
};

type Group = keyof Pick<
  RetailerNetworkTobaccoProgramPerformanceFragment,
  'loyaltyMetrics' | 'memberMetrics' | 'multiUnitMetrics'
>;

const catalog: Record<Group, CatalogEntry[]> = {
  memberMetrics: [
    {
      type: RetailerNetworkTobaccoProgramMetricType.TotalMembers,
      label: 'Total Members',
      getValue: (performance) => performance.memberMetrics.totalMembers,
    },
    {
      type: RetailerNetworkTobaccoProgramMetricType.NewMembers,
      label: 'New Members',
      getValue: (performance) => performance.memberMetrics.totalNewMembers,
    },
    {
      type: RetailerNetworkTobaccoProgramMetricType.ActiveMembers,
      label: 'Active Members',
      getValue: (performance) => performance.memberMetrics.totalActiveMembers,
    },
    {
      type: RetailerNetworkTobaccoProgramMetricType.RetentionRate,
      label: 'Retention Rate',
      valueSuffix: '%',
      getValue: (performance) => performance.memberMetrics.retentionRate,
    },
  ],
  loyaltyMetrics: [
    {
      type: RetailerNetworkTobaccoProgramMetricType.LoyaltyTransactions,
      label: 'Transactions',
      fullLabel: 'Loyalty Transactions',
      getValue: (performance) => performance.loyaltyMetrics.totalTransactions,
    },
    {
      type: RetailerNetworkTobaccoProgramMetricType.LoyaltyEstimatedReimbursement,
      label: 'Estimated Reimbursement',
      fullLabel: 'Loyalty Estimated Reimbursement',
      valueType: 'currency',
      getValue: (performance) => performance.loyaltyMetrics.estimatedReimbursements,
    },
    {
      type: RetailerNetworkTobaccoProgramMetricType.LoyaltyAvgCustomerSavings,
      label: 'Average Customer Savings',
      fullLabel: 'Loyalty Average Customer Savings',
      valueType: 'currency',
      getValue: (performance) => performance.loyaltyMetrics.avgCustomerSavings,
    },
    {
      type: RetailerNetworkTobaccoProgramMetricType.LoyaltyAvgMarketBasketSize,
      label: 'Average Market Basket Size',
      fullLabel: 'Loyalty Average Market Basket Size',
      valueSuffix: ' Units',
      getValue: (performance) => performance.loyaltyMetrics.basketSize,
    },
  ],
  multiUnitMetrics: [
    {
      type: RetailerNetworkTobaccoProgramMetricType.MultiUnitTransactions,
      label: 'Transactions',
      fullLabel: 'Multi-Unit Transactions',
      getValue: (performance) => performance.multiUnitMetrics.totalTransactions,
    },
    {
      type: RetailerNetworkTobaccoProgramMetricType.MultiUnitEstimatedReimbursement,
      label: 'Estimated Reimbursement',
      fullLabel: 'Multi-Unit Estimated Reimbursement',
      valueType: 'currency',
      getValue: (performance) => performance.multiUnitMetrics.estimatedReimbursements,
    },
    {
      type: RetailerNetworkTobaccoProgramMetricType.MultiUnitAvgCustomerSavings,
      label: 'Average Customer Savings',
      fullLabel: 'Multi-Unit Average Customer Savings',
      valueType: 'currency',
      getValue: (performance) => performance.multiUnitMetrics.avgCustomerSavings,
    },
    {
      type: RetailerNetworkTobaccoProgramMetricType.MultiUnitAvgMarketBasketSize,
      label: 'Average Market Basket Size',
      fullLabel: 'Multi-Unit Average Market Basket Size',
      valueSuffix: ' Units',
      getValue: (performance) => performance.multiUnitMetrics.basketSize,
    },
  ],
};

const getGroupCards = (
  group: CatalogEntry[],
  performance: RetailerNetworkTobaccoProgramPerformanceFragment,
  previousPerformance: RetailerNetworkTobaccoProgramPerformanceFragment,
): MetricCardProps<RetailerNetworkTobaccoProgramMetricType>[] => {
  return group.map((entry) => {
    const prevValue = entry.getValue(previousPerformance);
    const value = entry.getValue(performance);

    return {
      label: entry.label,
      payload: entry.type,
      value,
      prevValue,
      valueType: entry.valueType,
      valuePrefix: entry.valuePrefix,
      valueSuffix: entry.valueSuffix,
    };
  });
};

const getGroupedCards = (
  performance: RetailerNetworkTobaccoProgramPerformanceFragment,
  previousPerformance: RetailerNetworkTobaccoProgramPerformanceFragment,
) => {
  return {
    member: getGroupCards(catalog.memberMetrics, performance, previousPerformance),
    loyalty: getGroupCards(catalog.loyaltyMetrics, performance, previousPerformance),
    multiUnit: getGroupCards(catalog.multiUnitMetrics, performance, previousPerformance),
  };
};

const getFlatCards = (
  performance: RetailerNetworkTobaccoProgramPerformanceFragment,
  previousPerformance: RetailerNetworkTobaccoProgramPerformanceFragment,
): MetricCardProps<RetailerNetworkTobaccoProgramMetricType>[] => {
  return (Object.values(catalog) as CatalogEntry[][]).flatMap((group) =>
    group.map((entry) => {
      const prevValue = entry.getValue(previousPerformance);
      const value = entry.getValue(performance);

      return {
        label: entry.fullLabel ?? entry.label,
        payload: entry.type,
        value,
        prevValue,
        valueType: entry.valueType,
        valuePrefix: entry.valuePrefix,
        valueSuffix: entry.valueSuffix,
      };
    }),
  );
};

export const getTobaccoPerformanceMetrics = (
  performance: RetailerNetworkTobaccoProgramPerformanceFragment,
  previousPerformance: RetailerNetworkTobaccoProgramPerformanceFragment,
) => {
  return {
    grouped: getGroupedCards(performance, previousPerformance),
    flat: getFlatCards(performance, previousPerformance),
  };
};
